import { BackendHttpError, retryBackendRequestForResidency } from './BackendRetry';

export enum SupportedDataResidency {
  USA = 'USA',
  ARE = 'ARE',
}

export interface DataResidencyConfig {
  dataResidency: SupportedDataResidency;
  cognitoAwsRegion: string;
  dynamoDbAwsRegion: string;
  lambdaAwsRegion: string;
  apiEndpointSuffix: string;
}

export const DATA_RESIDENCY_CONFIGS: Record<SupportedDataResidency, DataResidencyConfig> = {
  [SupportedDataResidency.USA]: {
    dataResidency: SupportedDataResidency.USA,
    cognitoAwsRegion: 'us-east-1',
    dynamoDbAwsRegion: 'us-west-2',
    lambdaAwsRegion: 'us-east-1',
    apiEndpointSuffix: '',
  },
  [SupportedDataResidency.ARE]: {
    dataResidency: SupportedDataResidency.ARE,
    cognitoAwsRegion: 'eu-central-1',
    dynamoDbAwsRegion: 'eu-central-1',
    lambdaAwsRegion: 'eu-central-1',
    apiEndpointSuffix: '-fra',
  },
};

// Resolves a stored data residency value, migrating legacy lowercase values
// ('us'/'uae') to their ISO 3166-1 alpha-3 equivalents ('USA'/'ARE').
function resolveStoredDataResidency(storedValue: string | null): SupportedDataResidency | null {
  if (storedValue === 'us') {
    return SupportedDataResidency.USA;
  }
  if (storedValue === 'uae') {
    return SupportedDataResidency.ARE;
  }
  if (storedValue && Object.values(SupportedDataResidency).includes(storedValue as SupportedDataResidency)) {
    return storedValue as SupportedDataResidency;
  }
  return null;
}

export class DataResidencyManager {
  private static instance: DataResidencyManager;
  private currentDataResidency: SupportedDataResidency = SupportedDataResidency.USA;

  private constructor() {}

  public static getInstance(): DataResidencyManager {
    if (!DataResidencyManager.instance) {
      DataResidencyManager.instance = new DataResidencyManager();
    }
    return DataResidencyManager.instance;
  }

  public setDataResidency(dataResidency: SupportedDataResidency): void {
    this.currentDataResidency = dataResidency;
    localStorage.setItem('peekapak.userDataResidency', dataResidency);
  }

  public getCurrentDataResidency(): SupportedDataResidency {
    // Check localStorage first
    const storedDataResidency = localStorage.getItem('peekapak.userDataResidency');
    const resolvedDataResidency = resolveStoredDataResidency(storedDataResidency);
    if (resolvedDataResidency) {
      if (resolvedDataResidency !== storedDataResidency) {
        // Persist migrated legacy value
        this.setDataResidency(resolvedDataResidency);
      } else {
        this.currentDataResidency = resolvedDataResidency;
      }
    }

    return this.currentDataResidency;
  }

  public getCurrentConfig(): DataResidencyConfig {
    return DATA_RESIDENCY_CONFIGS[this.getCurrentDataResidency()];
  }

  public isAREDataResidency(): boolean {
    return this.getCurrentDataResidency() === SupportedDataResidency.ARE;
  }

  public getApiSubdomain(): string {
    const config = this.getCurrentConfig();
    return config.apiEndpointSuffix === '-fra' ? 'api-fra' : 'api';
  }

  public getCognitoConfiguration(dataResidency?: SupportedDataResidency) {
    // Use provided data residency or fall back to current
    const targetDataResidency = dataResidency || this.getCurrentDataResidency();

    if (targetDataResidency === SupportedDataResidency.ARE) {
      if (import.meta.env.MODE === 'development') {
        return import('@peekapak/common/configuration/devCognitoConfigurationFRA.json');
      } else {
        return import('@peekapak/common/configuration/prodCognitoConfigurationFRA.json');
      }
    } else {
      if (import.meta.env.MODE === 'development') {
        return import('@peekapak/common/configuration/devCognitoConfiguration.json');
      } else {
        return import('@peekapak/common/configuration/prodCognitoConfiguration.json');
      }
    }
  }

  public async detectUserDataResidencyFromIP(): Promise<SupportedDataResidency> {
    try {
      // Use a geolocation API to determine user's required data residency
      const response = await retryBackendRequestForResidency(
        'DataResidencyManager.detectUserDataResidencyFromIP',
        async () => {
          const result = await window.fetch('/api/commonservices/users/get-country-origin');

          if (!result.ok) {
            throw new BackendHttpError(`HTTP ${result.status}: ${result.statusText}`, result.status);
          }

          return result;
        },
      );
      const data = await response.json();

      // Country codes that require ARE data residency (UAE + Qatar, served by the fra backend)
      const areCountryCodes = ['AE', 'QA', 'ARE', 'QAT'];

      if (data.countryCode && areCountryCodes.includes(data.countryCode.toUpperCase())) {
        return SupportedDataResidency.ARE;
      }

      return SupportedDataResidency.USA;
    } catch (error) {
      console.warn('Failed to detect user data residency, defaulting to USA:', error);
      return SupportedDataResidency.USA;
    }
  }

  public async initializeDataResidency(): Promise<void> {
    // Check if data residency is already set in localStorage
    const storedDataResidency = resolveStoredDataResidency(localStorage.getItem('peekapak.userDataResidency'));
    if (storedDataResidency) {
      this.setDataResidency(storedDataResidency);
      return;
    }

    // Detect data residency from IP if not set
    const detectedDataResidency = await this.detectUserDataResidencyFromIP();
    this.setDataResidency(detectedDataResidency);
  }
}

export const dataResidencyManager = DataResidencyManager.getInstance();
